import { ChangeDetectionStrategy, Component, OnInit, computed, effect, inject, signal, untracked } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { SharedModule } from '@/shared.module';
import { NnnService } from '@/services';
import type { NicActiviteit, NicInterventieSummary } from '@/services';
import type {
  EditNnnInterventionDialogData,
  EditNnnInterventionDialogResult,
  NnnKnowledgeDomein,
  NnnKnowledgeKlasse,
} from '../../types/careplan-nnn.types';

type InterventionFilter = 'linked' | 'all';

/**
 * Dialog to add a NIC interventie to a careplan-NNN row, or to edit the
 * activity selection of one already on the row. When the row's NANDA
 * diagnosis is known, the list defaults to the interventies linked to that
 * diagnosis, with a toggle to show all. Closes with an
 * {@link EditNnnInterventionDialogResult}, or `undefined` on cancel.
 */
@Component({
  selector: 'app-edit-nnn-intervention-dialog',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './edit-nnn-intervention-dialog.component.html',
  styleUrl: './edit-nnn-intervention-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditNnnInterventionDialogComponent implements OnInit {
  private readonly nnnService = inject(NnnService);
  private readonly data = inject<EditNnnInterventionDialogData>(MAT_DIALOG_DATA);

  protected readonly isEdit = !!this.data.interventie;

  protected readonly interventies = signal<NicInterventieSummary[]>([]);
  protected readonly interventionFilter = signal<InterventionFilter>(this.data.diagnoseCode != null ? 'linked' : 'all');
  private readonly linkedCodes = signal<ReadonlySet<number> | null>(null);
  protected readonly searchText = signal('');
  protected readonly selected = signal<NicInterventieSummary | null>(this.data.interventie ?? null);
  protected readonly activiteiten = signal<NicActiviteit[]>([]);
  protected readonly checkedRefs = signal<ReadonlySet<string>>(new Set(this.data.selectedActivityRefs ?? []));
  protected readonly loadingList = signal(false);
  protected readonly loadingActiviteiten = signal(false);

  protected readonly loadingTree = signal(false);
  protected readonly nicDomeinen = signal<NnnKnowledgeDomein[]>([]);
  private readonly itemsByKlasse = signal<ReadonlyMap<string, NicInterventieSummary[]>>(new Map());
  private readonly loadingKlassen = signal<ReadonlySet<string>>(new Set());
  private readonly expandedDomeinen = signal<ReadonlySet<string>>(new Set());
  private readonly expandedKlassen = signal<ReadonlySet<string>>(new Set());
  private readonly klasseLoads = new Map<string, Promise<void>>();
  private treeLoad: Promise<void> | null = null;
  private readonly excludeRefs = new Set(this.data.excludeRefs ?? []);

  protected readonly showFilterToggle = computed(() => (this.linkedCodes()?.size ?? 0) > 0);

  protected readonly showTree = computed(
    () => this.interventionFilter() === 'all' && this.searchText().trim().length === 0
  );

  protected readonly filteredInterventies = computed(() => {
    const query = this.searchText().trim().toLowerCase();
    const linked = this.interventionFilter() === 'linked' ? this.linkedCodes() : null;
    return this.interventies().filter(
      i =>
        !this.excludeRefs.has(String(i.code)) &&
        (!linked || linked.has(i.code)) &&
        (!query || i.omschrijving.toLowerCase().includes(query) || i.code.toString().includes(query))
    );
  });

  constructor() {
    effect(() => {
      if (!this.isEdit && this.interventionFilter() === 'all') {
        untracked(() => void this.ensureNicTree());
      }
    });
  }

  ngOnInit(): void {
    if (!this.isEdit) {
      void this.loadInterventies();
      if (this.data.diagnoseCode != null) {
        void this.loadLinkedInterventies(this.data.diagnoseCode);
      }
    }
    const preset = this.selected();
    if (preset) {
      void this.loadActiviteiten(preset);
    }
  }

  private async loadInterventies(): Promise<void> {
    this.loadingList.set(true);
    const { data } = await this.nnnService.getNicInterventions(undefined, true);
    this.loadingList.set(false);
    if (data) {
      this.interventies.set(data);
    }
  }

  private async loadLinkedInterventies(diagnoseCode: number): Promise<void> {
    const { data } = await this.nnnService.getNandaDiagnoseInterventies(diagnoseCode);
    if (data && data.length > 0) {
      this.linkedCodes.set(new Set(data.map(i => i.code)));
    } else {
      this.interventionFilter.set('all');
    }
  }

  protected onFilterChange(filter: InterventionFilter): void {
    this.interventionFilter.set(filter);
  }

  private ensureNicTree(): Promise<void> {
    if (this.nicDomeinen().length > 0) {
      return Promise.resolve();
    }
    this.treeLoad ??= this.loadNicTree().finally(() => {
      this.treeLoad = null;
    });
    return this.treeLoad;
  }

  private async loadNicTree(): Promise<void> {
    this.loadingTree.set(true);
    const [domeinen, klassen] = await Promise.all([this.nnnService.getNicDomeinen(), this.nnnService.getNicKlassen()]);
    this.loadingTree.set(false);
    if (!domeinen.data || !klassen.data) {
      return;
    }
    const klasseData = klassen.data;
    this.nicDomeinen.set(
      domeinen.data.map(domein => ({
        key: `nic:${domein.id}`,
        label: String(domein.nummer ?? ''),
        omschrijving: domein.omschrijving,
        definitie: domein.definitie,
        klassen: klasseData
          .filter(klasse => klasse.domeinId === domein.id)
          .map(klasse => ({
            key: `nic:${klasse.id}`,
            label: klasse.letter,
            omschrijving: klasse.omschrijving,
            definitie: klasse.definitie,
          })),
      }))
    );
  }

  protected itemsFor(klasseKey: string): NicInterventieSummary[] | undefined {
    return this.itemsByKlasse().get(klasseKey);
  }

  protected isLoadingKlasse(klasseKey: string): boolean {
    return this.loadingKlassen().has(klasseKey);
  }

  protected isDomeinExpanded(domeinKey: string): boolean {
    return this.expandedDomeinen().has(domeinKey);
  }

  protected isKlasseExpanded(klasseKey: string): boolean {
    return this.expandedKlassen().has(klasseKey);
  }

  protected setDomeinExpanded(domeinKey: string, expanded: boolean): void {
    this.expandedDomeinen.update(keys => toggleKey(keys, domeinKey, expanded));
  }

  protected setKlasseExpanded(klasse: NnnKnowledgeKlasse, expanded: boolean): void {
    this.expandedKlassen.update(keys => toggleKey(keys, klasse.key, expanded));
    if (expanded) {
      void this.ensureKlasseItems(klasse);
    }
  }

  private ensureKlasseItems(klasse: NnnKnowledgeKlasse): Promise<void> {
    if (this.itemsByKlasse().has(klasse.key)) {
      return Promise.resolve();
    }
    let load = this.klasseLoads.get(klasse.key);
    if (!load) {
      load = this.loadKlasseItems(klasse).finally(() => this.klasseLoads.delete(klasse.key));
      this.klasseLoads.set(klasse.key, load);
    }
    return load;
  }

  private async loadKlasseItems(klasse: NnnKnowledgeKlasse): Promise<void> {
    this.loadingKlassen.update(keys => toggleKey(keys, klasse.key, true));
    const klasseId = Number(klasse.key.split(':')[1]);
    const { data } = await this.nnnService.getNicInterventions(undefined, true, klasseId);
    this.loadingKlassen.update(keys => toggleKey(keys, klasse.key, false));
    if (data) {
      this.itemsByKlasse.update(items =>
        new Map(items).set(
          klasse.key,
          data.filter(i => !this.excludeRefs.has(String(i.code)))
        )
      );
    }
  }

  private async loadActiviteiten(interventie: NicInterventieSummary): Promise<void> {
    this.loadingActiviteiten.set(true);
    const { data } = await this.nnnService.getNicActiviteiten(interventie.code);
    this.loadingActiviteiten.set(false);
    if (data && this.selected()?.code === interventie.code) {
      this.activiteiten.set(data);
    }
  }

  protected onSearchInput(event: Event): void {
    this.searchText.set((event.target as HTMLInputElement).value);
  }

  protected select(interventie: NicInterventieSummary): void {
    if (this.selected()?.code === interventie.code) {
      return;
    }
    this.selected.set(interventie);
    this.activiteiten.set([]);
    this.checkedRefs.set(new Set());
    void this.loadActiviteiten(interventie);
  }

  protected isChecked(activiteit: NicActiviteit): boolean {
    return this.checkedRefs().has(String(activiteit.code));
  }

  protected toggleActiviteit(activiteit: NicActiviteit): void {
    const ref = String(activiteit.code);
    this.checkedRefs.update(refs => {
      const next = new Set(refs);
      if (next.has(ref)) {
        next.delete(ref);
      } else {
        next.add(ref);
      }
      return next;
    });
  }

  protected buildResult(): EditNnnInterventionDialogResult | null {
    const interventie = this.selected();
    if (!interventie) {
      return null;
    }
    const checked = this.checkedRefs();
    const knownRefs = this.activiteiten()
      .map(activiteit => String(activiteit.code))
      .filter(ref => checked.has(ref));
    const unknownRefs = [...checked].filter(ref => !knownRefs.includes(ref));
    return {
      interventie,
      activityRefs: [...knownRefs, ...unknownRefs],
      activiteiten: this.activiteiten(),
    };
  }
}

function toggleKey(keys: ReadonlySet<string>, key: string, present: boolean): ReadonlySet<string> {
  const next = new Set(keys);
  if (present) {
    next.add(key);
  } else {
    next.delete(key);
  }
  return next;
}
